/**
 * InfoPanel 组件
 *
 * 功能：右侧信息面板，展示照片的详细信息
 * 包含：
 * - 作者信息
 * - 照片标签展示与添加
 * - 水印信息展示
 * - 去水印功能入口
 * - 所属文件夹信息
 * - 添加到文件夹功能
 * - 评论区功能
 */
import React, { useEffect, useRef, useState } from 'react'
import { Spin, Empty, Button, message } from 'antd'
import { useAppSelector } from '../../../../app/hooks'
import { usePhotoLabelEnabled } from '../../../../app/hooks/usePhotoLabelEnabled'
import { folderDetailPath } from '../../../../utils/aiFolderClassify'
import {
  fetchManageData,
  batchBindLabels,
  batchUnbindLabels
} from '../../../photoTag/api'
import sensors from '../../../../utils/sensors'
import { trackLabelBindSuccess } from '../../PhotoFilter/utils/sensorUtils'
import {
  trackPicspageClick,
  trackPicspageResult,
  trackPicspageToast
} from '../../../photoTag/utils/track'
import type { PhotoTag } from '../../../photoTag/types'
import PhotoComments from '../../PhotoComments'
import AddToFolder from '../../AddToFolder'
import folderIcon from '../../../../images/folder_icon.png'

interface Member {
  headimgurl?: string
  nickname?: string
}

interface Watermark {
  contents: {
    title: string
    content: string
  }[]
}

interface Folder {
  folderID: string
  folderName: string
}

interface Comment {
  id?: string
  content?: string
  [key: string]: any
}

interface Photo {
  comments?: Comment[]
  labels?: Array<{ id: string; name: string; color: string }>
  file?: string
  [key: string]: any
}

interface InfoPanelProps {
  isFullScreen?: boolean
  isHideInfo?: boolean
  watermarkLoading?: boolean
  watermarks?: Watermark[]
  member?: Member | null
  isShowFolders?: boolean
  folders?: Folder[]
  isShowAddToFolderBtn?: boolean
  isShowComments?: boolean
  photo?: Photo
  canRemoveWatermark?: boolean
  onViewByPerson?: (member: any) => void
  onRemoveWatermark: () => void
  onAddToFolder: ({
    folderIDs,
    submitCallback
  }: {
    folderIDs: number[]
    submitCallback: () => void
  }) => void
  onSaveComment: (value: string) => void
  onReportSA: (action: string, folderID?: string, folderName?: string) => void
  onRemoveEventListener: () => void
  onAddEventListener: () => void
}

// 照片标签区块（含添加功能）
const PhotoLabelSection: React.FC<{ photo: Photo }> = ({ photo }) => {
  const groupID = useAppSelector((state) => state.group.groupId)
  const userID = useAppSelector((state) => state.main.user?.user_id)

  const [allTags, setAllTags] = useState<PhotoTag[]>([])
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [adding, setAdding] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // 当前照片已有的标签
  const getCurrentLabels = (): {
    id: string
    name: string
    color: string
  }[] => {
    const labels = photo.labels
    return Array.isArray(labels) ? labels : []
  }

  const [currentLabels, setCurrentLabels] = useState(getCurrentLabels)

  // photo 切换时同步
  useEffect(() => {
    setCurrentLabels(getCurrentLabels())
    setDropdownOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo])

  // 打开下拉时懒加载全量标签
  useEffect(() => {
    if (!dropdownOpen || allTags.length > 0 || !groupID || !userID) return
    fetchManageData(groupID, userID).then((data) => {
      const tTags = (data.team?.groups || []).flatMap((g) => g.labels || [])
      const pTags = (data.custom?.groups || []).flatMap((g) => g.labels || [])
      setAllTags(
        [...tTags, ...pTags].map((l) => ({
          id: String(l.id),
          name: l.name,
          color: l.color,
          scope: l.scope === 'custom' ? 'personal' : 'team'
        }))
      )
    })
  }, [dropdownOpen, groupID, userID])

  // 点击外部关闭
  useEffect(() => {
    if (!dropdownOpen) return
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [dropdownOpen])

  const handleAddLabel = async (tag: PhotoTag) => {
    const alreadyHas = currentLabels.some((l) => l.id === tag.id)
    if (alreadyHas) return

    const file = photo.file
    if (!file || !groupID) return

    setAdding(true)
    const fromPageForTrack = 'photov2' as const
    const t0 = Date.now()
    try {
      // 双保险: 后端 exclusive 可能没部署 → 先 unbind 当前所有再 bind 新的
      const oldIds = currentLabels.map((l) => String(l.id))
      if (oldIds.length > 0) {
        await batchUnbindLabels({
          groupID,
          filter: { include: [file], exclude: [] },
          labelIDs: oldIds
        })
      }
      await batchBindLabels({
        groupID,
        filter: { include: [file], exclude: [] },
        labelIDs: [tag.id],
        exclusive: true
      })
      trackLabelBindSuccess({
        photoNum: 1,
        labelName: tag.name,
        fromPlace: '照片预览页打标签'
      })
      // v3 新埋点: result + toast
      trackPicspageResult({
        actionName: 'bind_label_single_exclusive',
        actionResult: 'success',
        costTime: Date.now() - t0,
        photoNum: 1,
        number1: 1,
        list1: [tag.name],
        tabName: 'team_label',
        bool1: true,
        fromPage: fromPageForTrack
      })
      trackPicspageToast({
        toastName: 'single_replace_success',
        fromPage: fromPageForTrack
      })
      setCurrentLabels([
        { id: tag.id, name: tag.name, color: tag.color || '#2979FF' }
      ])
      setDropdownOpen(false)
    } catch (err) {
      const msg = (err as any).message || '添加标签失败'
      message.error(msg)
      trackPicspageResult({
        actionName: 'bind_label_single_exclusive',
        actionResult: 'fail',
        costTime: Date.now() - t0,
        errorMsg: msg,
        photoNum: 1,
        number1: 1,
        list1: [tag.name],
        tabName: 'team_label',
        bool1: true,
        fromPage: fromPageForTrack
      })
      trackPicspageToast({
        toastName: 'single_replace_fail',
        fromPage: fromPageForTrack
      })
    } finally {
      setAdding(false)
    }
  }

  const currentLabelIds = new Set(currentLabels.map((l) => l.id))
  const unaddedTags = allTags.filter((t) => !currentLabelIds.has(t.id))

  return (
    <div className="watermark info-panel-labels" style={{ paddingBottom: 12 }}>
      <h4>照片标签</h4>

      {/* 已打标签 */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
        {currentLabels.map((tag, idx) => (
          <span
            key={tag.id || idx}
            style={{
              display: 'inline-block',
              padding: '2px 8px',
              height: 24,
              lineHeight: '20px',
              fontSize: 12,
              fontWeight: 500,
              borderRadius: 4,
              color: tag.color || '#E53935',
              backgroundColor: `${tag.color || '#E53935'}1A`
            }}
          >
            {tag.name}
          </span>
        ))}

        {/* 添加按钮 */}
        <div style={{ position: 'relative' }} ref={dropdownRef}>
          <button
            onClick={() => {
              const willOpen = !dropdownOpen
              setDropdownOpen(willOpen)
              sensors.track('web_workgroup_preview_page_click', {
                clickItem: '添加标签',
                track_sign:
                  'aworkgroup.b4312.action.web_workgroup_preview_page_click'
              })
              // v4 新埋点: 预览页「+ 添加标签」点击 (仅展开时上报, 与老埋点并行)
              if (willOpen) {
                trackPicspageClick({
                  clickItem: 'preview_label_add',
                  fromPage: 'photov2'
                })
              }
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              height: 24,
              padding: '0 8px',
              fontSize: 12,
              color: '#999',
              background: 'none',
              border: '1px dashed #d9d9d9',
              borderRadius: 4,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'border-color 0.2s, color 0.2s'
            }}
            onMouseEnter={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.borderColor =
                '#2979ff'
              ;(e.currentTarget as HTMLButtonElement).style.color = '#2979ff'
            }}
            onMouseLeave={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.borderColor =
                '#d9d9d9'
              ;(e.currentTarget as HTMLButtonElement).style.color = '#999'
            }}
          >
            + 添加标签
          </button>

          {/* 下拉面板 */}
          {dropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: 28,
                left: 0,
                zIndex: 100,
                background: '#fff',
                borderRadius: 6,
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                border: '1px solid #f0f0f0',
                minWidth: 160,
                maxHeight: 220,
                overflowY: 'auto',
                padding: '4px 0'
              }}
            >
              {adding && (
                <div
                  style={{ padding: '8px 12px', fontSize: 12, color: '#999' }}
                >
                  添加中...
                </div>
              )}
              {!adding && unaddedTags.length === 0 && (
                <div
                  style={{ padding: '8px 12px', fontSize: 12, color: '#bbb' }}
                >
                  {allTags.length === 0 ? '加载中...' : '暂无可添加的标签'}
                </div>
              )}
              {!adding &&
                unaddedTags.map((tag) => (
                  <div
                    key={tag.id}
                    onClick={() => handleAddLabel(tag)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '7px 12px',
                      fontSize: 13,
                      cursor: 'pointer',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={(e) => {
                      ;(e.currentTarget as HTMLDivElement).style.background =
                        '#f5f7ff'
                    }}
                    onMouseLeave={(e) => {
                      ;(e.currentTarget as HTMLDivElement).style.background =
                        'transparent'
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: tag.color || '#2979FF',
                        flexShrink: 0
                      }}
                    />
                    <span style={{ color: '#333' }}>{tag.name}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const InfoPanel: React.FC<InfoPanelProps> = ({
  isFullScreen,
  isHideInfo,
  watermarkLoading,
  watermarks,
  member,
  isShowFolders,
  folders,
  isShowAddToFolderBtn,
  isShowComments,
  photo,
  canRemoveWatermark,
  onViewByPerson,
  onRemoveWatermark,
  onAddToFolder,
  onSaveComment,
  onReportSA,
  onRemoveEventListener,
  onAddEventListener
}) => {
  const photoLabelEnabled = usePhotoLabelEnabled()

  return (
    <div
      className="info-panel"
      style={isFullScreen || isHideInfo ? { width: 0 } : {}}
    >
      <Spin spinning={watermarkLoading} className="center">
        <div style={{ height: '100vh' }}>
          {watermarks && watermarks.length ? (
            <div className="info-content" onScroll={(e) => e.stopPropagation()}>
              <div className="author">
                <img
                  src={member?.headimgurl}
                  alt=""
                  onClick={() =>
                    member && onViewByPerson && onViewByPerson(member)
                  }
                />
                <span>{member?.nickname}</span>
              </div>

              {/* 照片标签（KV 白名单内展示） */}
              {photoLabelEnabled && photo && (
                <PhotoLabelSection photo={photo} />
              )}

              {/* 水印信息 */}
              {(watermarks || []).map((watermark, index) => (
                <div className="watermark" key={index}>
                  <h4>水印信息</h4>
                  {watermark.contents.map((content, contentIndex) => {
                    if (content.title.trim() || content.content.trim()) {
                      return (
                        <div className="wm-item" key={contentIndex}>
                          <div className="wm-title">{content.title}</div>
                          <div>：</div>
                          <div className="wm-content">{content.content}</div>
                        </div>
                      )
                    } else {
                      return null
                    }
                  })}

                  {canRemoveWatermark && (
                    <Button
                      style={{ margin: '8px 0' }}
                      onClick={onRemoveWatermark}
                      type="primary"
                      ghost
                    >
                      去水印
                    </Button>
                  )}
                </div>
              ))}

              {/* 文件夹信息 */}
              {isShowFolders && (
                <div
                  className="folders watermark"
                  style={{ paddingBottom: '7px' }}
                >
                  <h4>所属文件夹</h4>
                  <div className="container">
                    {(folders || []).map(({ folderID, folderName }) => (
                      <div
                        key={folderID}
                        className="folder"
                        onClick={() => {
                          onReportSA('clickFolder', folderID, folderName)
                          window.location.href = folderDetailPath(folderID)
                        }}
                      >
                        <img src={folderIcon} className="folder-icon" alt="" />
                        {folderName}
                      </div>
                    ))}
                  </div>

                  {isShowAddToFolderBtn && (
                    <AddToFolder
                      onSubmit={(params) =>
                        onAddToFolder({
                          folderIDs: params.folderIDs,
                          submitCallback: params.submitCallback
                        })
                      }
                      place="previewPhoto"
                      style={{
                        color: '#0093FF',
                        borderColor: '#0093FF'
                      }}
                      source={3}
                      sensorReport={() => onReportSA('addFolder')}
                    />
                  )}
                </div>
              )}

              {/* 评论区 */}
              {isShowComments && (
                <div
                  onFocus={onRemoveEventListener}
                  onBlur={onAddEventListener}
                >
                  <h4>评论</h4>
                  <PhotoComments
                    currentPhoto={photo}
                    location="sidebar"
                    themeColor="drawerComments"
                    handleSaveComment={onSaveComment}
                    handleReportSA={onReportSA}
                    comments={photo?.comments || []}
                  />
                </div>
              )}
            </div>
          ) : (
            <Empty description="无水印数据" className="center" />
          )}
        </div>
      </Spin>
    </div>
  )
}

export default InfoPanel
